import type { MailboxInfo } from "owa-client-types";
import { trace } from "owa-trace";
import type { PersonSearchField } from "./getEntityAwareQuery";

/**
 * A single entity the matcher selected for a query. Carries the matched query
 * text and the resolved person identity, so it is PII and must only be surfaced
 * through a user-consented channel (e.g. clipboard) — never logged to telemetry.
 */
export interface EntityRewriteMatchedEntity {
    readonly matchedText: string;
    readonly displayName: string;
    readonly email: string;
    /** Match layer that produced this entity: EXACT | PREFIX | TYPO. */
    readonly layer: string;
    /** Match confidence in [0, 1]: coverage of the target name weighted by match reliability. */
    readonly score: number;
    /** The person's affinity (edge weight) in [0, 1]; used as the ranking tie-break. */
    readonly affinity: number;
    /**
     * The actual value the selection uses to rank this candidate:
     * `score + affinity × 0.01`. It can slightly exceed 1 because the affinity
     * tie-break is added on top of the [0, 1] match confidence.
     */
    readonly rankingScore: number;
}

/**
 * Explains why the entity-recognition rewrite did (or did not) fire for a query.
 *
 * The scalar "reason-code" fields are non-PII and safe for telemetry. The
 * `rawQuery`, `rewrittenQuery`, and `matchedEntities` fields can contain query
 * text and person identities (PII) and must only leave the device through a
 * user-consented channel.
 */
export interface EntityRewriteDiagnostics {
    // --- Non-PII reason codes (safe to log) ---
    /** Whether a people-field rewrite was produced for this query. */
    readonly rewriteApplied: boolean;
    /** Why the rewrite was skipped, when it was. */
    readonly skippedReason?: string;
    /** Number of entities selected. */
    readonly entityCount: number;
    /** Whitespace-delimited token count of the raw query. */
    readonly queryTokenCount: number;
    /** Stop-word (verb + filler) token count from the analysis pass. */
    readonly stopWordCount: number;
    /** Number of residual (non-entity) keyword tokens. */
    readonly residualKeywordCount: number;
    /** Match layer of the highest-scoring selected entity. */
    readonly topMatchLayer?: string;
    /** Character length of the query text the top entity matched. */
    readonly topMatchedTextLength?: number;
    /** Character length of the target name the top entity matched against. */
    readonly topTargetNameLength?: number;
    /** Score of the highest-scoring selected entity, as a [0, 1] match confidence. */
    readonly topScore?: number;
    /** Affinity (edge weight) of the highest-scoring selected entity, in [0, 1]. */
    readonly topAffinity?: number;
    /** Ranking value of the top entity (`topScore + topAffinity × 0.01`); may exceed 1. */
    readonly topRankingScore?: number;
    
    // --- PII detail (clipboard / user-consented only, never logged) ---
    readonly rawQuery: string;
    readonly rewrittenQuery?: string;
    readonly matchedEntities: readonly EntityRewriteMatchedEntity[];
}

type EntityRewriteDiagnosticsProvider = (queryString: string, mailboxInfo: MailboxInfo, personSearchFields: readonly PersonSearchField[]) => EntityRewriteDiagnostics;

let registeredProvider: EntityRewriteDiagnosticsProvider | undefined;

/**
 * Register the matcher-backed diagnostics provider. Called from the lazy
 * entity-recognition module so the heavy matcher pipeline is not pulled into the
 * eager search bundle (mirrors {@link registerEntityQueryRewriter}).
 */
export function registerEntityRewriteDiagnosticsProvider(provider: EntityRewriteDiagnosticsProvider | undefined): void {
    registeredProvider = provider;
}

/**
 * Compute why the entity-recognition rewrite fired for a query, or `undefined`
 * when recognition has not been initialized for the account yet. Deterministic
 * and side-effect free.
 */
export function getEntityRewriteDiagnostics(queryString: string, mailboxInfo: MailboxInfo, personSearchFields: readonly PersonSearchField[] = ["From"]): EntityRewriteDiagnostics | undefined {
    if (!registeredProvider) {
        return undefined;
    }
    
    try {
        return registeredProvider(queryString, mailboxInfo, personSearchFields);
    }
    catch {
        trace.warn("Entity recognition diagnostics failed");
        return undefined;
    }
}
